import { render } from 'vue'

import { V_NOTIFICATION_PLACEMENTS, type VNotificationPlacement, type VNotificationType } from '../../types.ts'
import { mount } from '../../utils/mount.ts'
import VNotification, { type VNotificationExposed } from './VNotification.vue'

export { VNotification }

const containers = {} as Record<string, HTMLElement | undefined>
let containerZIndex = 2002

for (const placement of V_NOTIFICATION_PLACEMENTS) {
  containers[placement] = undefined
}

export interface NotificationOptions {
  clickThrough?: boolean
  closable?: boolean
  duration?: number
  message?: string
  onClick?: (notification: VNotificationExposed) => any
  onClose?: (notification: VNotificationExposed) => any
  placement?: VNotificationPlacement
  title?: string
  type?: VNotificationType
  loading?: boolean
  dangerouslyUseHTMLString?: boolean
}

/**
 * Create a new VNotification component and mount it in one of our containers
 * Note that some of the defaults here, are different from the default props of the component.
 */
function notification({
  clickThrough = false,
  closable = true,
  duration = 4500,
  message = '',
  onClick = undefined,
  onClose = undefined,
  placement = 'top-right',
  title = '',
  type = '',
  loading = false,
  dangerouslyUseHTMLString = false,
}: NotificationOptions) {
  if (!(placement in containers)) {
    throw Error(`Invalid placement: ${placement}. Please use one of the following placements: ${V_NOTIFICATION_PLACEMENTS.join(', ')}`)
  }

  // If this is the first notification with this placement, a new container must be created.
  if (!containers[placement]) {
    const container = document.createElement('div')
    containers[placement] = container
    container.id = `v-notifications-${placement}`
    container.style.zIndex = `${containerZIndex}`
    document.body.appendChild(container)
  }

  let { vNode, el } = mount(VNotification, {
    name: 'VNotification',
    container: containers[placement],
    props: {
      clickThrough,
      closable,
      duration,
      message,
      onClick,
      onClose,
      placement,
      title,
      type,
      loading,
      dangerouslyUseHTMLString,
    },
  })

  if (vNode.component?.exposed) {
    vNode.component.exposed.destroy.value = () => {
      render(null, el)
      el.remove()
      // @ts-expect-error were cleaning up to avoid memory leaks.
      vNode = null
      // @ts-expect-error were cleaning up to avoid memory leaks.
      el = null
    }
  }

  return vNode
}

export function $notify(options: NotificationOptions) {
  return notification(options)
}

$notify.config = function ({ zIndex }: { zIndex: number }) {
  containerZIndex = zIndex

  for (const container of Object.values(containers)) {
    if (container) {
      container.style.zIndex = `${zIndex}`
    }
  }
}

const messageOptions = { placement: 'top' } as NotificationOptions

export function $message(message: NotificationOptions | string) {
  return typeof message === 'string' ? notification({ ...messageOptions, message }) : notification({ ...messageOptions, ...message })
}

$message.success = function (message: NotificationOptions | string) {
  return typeof message === 'string'
    ? notification({ ...messageOptions, message, type: 'success' } as NotificationOptions)
    : notification({ ...messageOptions, ...message, type: 'success' })
}

$message.warning = function (message: NotificationOptions | string) {
  return typeof message === 'string'
    ? notification({ ...messageOptions, message, type: 'warning' } as NotificationOptions)
    : notification({ ...messageOptions, ...message, type: 'warning' })
}

$message.error = function (message: NotificationOptions | string) {
  return typeof message === 'string'
    ? notification({ ...messageOptions, message, type: 'error' } as NotificationOptions)
    : notification({ ...messageOptions, ...message, type: 'error' })
}
